
import React, { useMemo, useCallback, memo, useEffect, useState } from 'react';
import { useModernTemplateHooks } from '../hooks/useModernTemplateHooks';
import { BookingSuccessScreen } from './BookingSuccessScreen';
import { MobileBookingLayout } from './MobileBookingLayout';
import { DesktopBookingLayout } from './DesktopBookingLayout';
import type { ModernTemplateProps } from '../types';
import { useBookingStateSync } from '../hooks/useBookingStateSync';
import { getTodayInTimezone } from '@/lib/timezone/date-utils';
import { useBookingContext } from '@/contexts/BookingContext';

export const ModernTemplateLogic: React.FC<ModernTemplateProps> = memo((props) => {
  const {
    meetingLink,
    isDebugMeeting = false,
    troubleshootMode = false,
    forceMobileLayout = false
  } = props;

  // Auto-selection state
  const [hasAutoSelected, setHasAutoSelected] = useState<boolean>(false);

  // Get booking context for reschedule mode (optional - only if BookingProvider exists)
  let isRescheduleMode = false;
  let isCancelMode = false;
  
  try {
    const bookingContext = useBookingContext();
    isRescheduleMode = bookingContext.isRescheduleMode;
    isCancelMode = bookingContext.isCancelMode;
  } catch (error) {
    // BookingProvider not found - this is fine for preview/edit pages
  }

  // Return error state immediately if no meeting link (before any hooks)
  if (!meetingLink) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Meeting Link Not Found</h2>
          <p className="text-muted-foreground">Unable to load meeting details.</p>
        </div>
      </div>
    );
  }

  // Use enhanced hooks when show_all_slots is enabled
  const shouldUseEnhanced = true; // Always fetch enhanced slots for optimal times

  // Only log in development and troubleshoot mode
  if (process.env.NODE_ENV === 'development' && troubleshootMode) {
    console.log('[ModernTemplateLogic] Enhanced mode:', shouldUseEnhanced, 'show_all_slots:', meetingLink?.show_all_slots);
  }

  // Get all hooks data with stable memoization - ALWAYS call hooks
  const {
    isMobile,
    fontClass,
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    debugSettings,
    setSelectedTimezone,
    handleDateSelected,
    handleTimeSelected,
    handleBack,
    themeCustomizations,
    formData,
    customAnswers,
    guests,
    isSubmitted,
    bookingId,
    setGuests,
    setIsSubmitted,
    setBookingId,
    handleInputChange,
    handleCustomAnswerChange,
    submitBooking,
    loading,
    error,
    availableSlots,
    isDayAvailable,
    slotsLoading,
    shouldShowSkeleton,
    organizerTimezone,
    preloadCalendarMonth,
    enhancedAvailableSlots
  } = useModernTemplateHooks({
    ...props,
    meetingLink, // Use real meeting link since we know it exists
    useEnhancedSlots: shouldUseEnhanced
  });

  // Sync template state with booking context for management actions
  useBookingStateSync(selectedDate, selectedTime);

  // Auto-selection logic - runs after availability is determined
  useEffect(() => {
    // Skip auto-selection for reschedule/cancel mode - let existing booking date be shown
    // Skip auto-selection on mobile devices - let users manually pick dates
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    
    if (!slotsLoading && !hasAutoSelected && !selectedDate && !isDebugMeeting && !isRescheduleMode && !isCancelMode && !isMobile) {
      const timezone = organizerTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get today's date in the organizer's timezone
      const todayInOrganizerTz = getTodayInTimezone(timezone);
      
      console.log('[ModernTemplateLogic] Auto-selection check - Today:', todayInOrganizerTz.toISOString());
      
      // Check if today is available
      const isTodayAvailable = isDayAvailable(todayInOrganizerTz);
      console.log('[ModernTemplateLogic] Is today available?', isTodayAvailable);
      
      if (isTodayAvailable) {
        console.log('[ModernTemplateLogic] Auto-selecting today:', todayInOrganizerTz.toISOString());
        handleDateSelected(todayInOrganizerTz);
        setHasAutoSelected(true);
      } else {
        // Find first available date in the next 30 days
        const availableDates = [];
        const today = new Date();
        for (let i = 0; i < 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          availableDates.push(date);
        }
        
        for (const date of availableDates) {
          if (isDayAvailable(date)) {
            console.log('[ModernTemplateLogic] Auto-selecting first available date:', date.toISOString());
            handleDateSelected(date);
            setHasAutoSelected(true);
            break;
          }
        }
      }
    }
  }, [slotsLoading, hasAutoSelected, selectedDate, isDebugMeeting, organizerTimezone, isDayAvailable, handleDateSelected, isRescheduleMode, isCancelMode]);

  // Log enhanced slots data for debugging only in dev mode
  if (process.env.NODE_ENV === 'development' && troubleshootMode) {
    console.log('[ModernTemplateLogic] Enhanced slots data:', enhancedAvailableSlots);
    console.log('[ModernTemplateLogic] Available slots:', availableSlots);
    console.log('[ModernTemplateLogic] Selected date:', selectedDate?.toDateString());
  }

  // Stable custom questions parsing
  const customQuestions = useMemo(() => {
    const questions = meetingLink?.custom_questions;
    
    if (!questions) return [];
    
    try {
      let parsedQuestions;
      
      if (typeof questions === 'string') {
        parsedQuestions = JSON.parse(questions);
      } else if (Array.isArray(questions)) {
        parsedQuestions = questions;
      } else {
        return [];
      }
      
      if (!Array.isArray(parsedQuestions)) return [];
      
      return parsedQuestions.filter(q => q && (q.id || q.question) && (q.question || q.label))
        .map(q => ({
          id: q.id || crypto.randomUUID(),
          question: q.question || q.label,
          type: q.type || q.field_type || 'text',
          required: Boolean(q.required),
          placeholder: q.placeholder || '',
          options: Array.isArray(q.options) ? q.options : []
        }));
    } catch (error) {
      return [];
    }
  }, [meetingLink?.custom_questions]);

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📝 Form submitted with data:', {
        formData,
        selectedDate,
        selectedTime,
        guests,
        customAnswers
      });
    }
    
    if (!formData.name || !formData.email || !selectedDate || !selectedTime) {
      console.error('❌ Missing required form data');
      return;
    }

    try {
      // CRITICAL FIX: selectedTime is in the invitee's selected timezone
      // We need to create a datetime in that timezone, then convert to UTC
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Create date string in format YYYY-MM-DD HH:mm:ss
      const { fromZonedTime } = await import('date-fns-tz');
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
      const localDateTimeStr = `${year}-${month}-${day} ${timeStr}`;
      
      // Convert from the selected timezone to UTC
      const utcDateTime = fromZonedTime(localDateTimeStr, selectedTimezone);

      const submissionData = {
        meeting_link_id: meetingLink.id,
        booker_name: formData.name,
        booker_email: formData.email,
        booker_company: formData.company,
        message: formData.message,
        booking_date: utcDateTime.toISOString(),
        timezone: selectedTimezone,
        guests: guests.map(g => ({
          name: g.name || '',
          email: g.email || '',
          phone: g.phone || ''
        })),
        responses: Object.entries(customAnswers).map(([questionId, value]) => ({
          question_id: questionId,
          response_value: value
        }))
      };

      console.log('[DEBUG] About to call submitBooking function from useBookingSubmission...');
      console.log('[DEBUG] submitBooking function:', typeof submitBooking);
      console.log('[DEBUG] Submission data:', submissionData);

      const result = await submitBooking(submissionData);

      console.log('[DEBUG] submitBooking result received:', result);

      // Store the booking ID for cancel/reschedule functionality
      if (result?.bookingId || result?.data?.id) {
        setBookingId(result.bookingId || result.data.id);
      }

      if (meetingLink.custom_redirect_url) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Redirecting to custom URL:', meetingLink.custom_redirect_url);
        }
        window.location.href = meetingLink.custom_redirect_url;
        return;
      }

      setIsSubmitted(true);

    } catch (error) {
      console.error("💥 Error submitting booking:", error);
    }
  }, [formData, selectedDate, selectedTime, selectedTimezone, meetingLink, guests, customAnswers, submitBooking, setIsSubmitted, setBookingId]);

  // Navigation handlers with stable references
  const handlePrevDay = useCallback(() => {
    if (!selectedDate) return;
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    handleDateSelected(prev);
    preloadCalendarMonth(prev);
  }, [selectedDate, handleDateSelected, preloadCalendarMonth]);

  const handleNextDay = useCallback(() => {
    if (!selectedDate) return;
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    handleDateSelected(next);
    preloadCalendarMonth(next);
  }, [selectedDate, handleDateSelected, preloadCalendarMonth]);

  // Memoized layout props to prevent re-renders
  const layoutProps = useMemo(() => ({
    meetingLink,
    fontClass,
    themeCustomizations,
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    handleBack,
    handleDateSelected,
    handleTimeSelected,
    setSelectedTimezone,
    handleFormSubmit,
    formData,
    customAnswers,
    guests,
    handleInputChange,
    handleCustomAnswerChange,
    setGuests,
    customQuestions,
    availableSlots,
    isDayAvailable,
    slotsLoading,
    shouldShowSkeleton,
    organizerTimezone,
    handlePrevDay,
    handleNextDay,
    debugSettings,
    troubleshootMode,
    loading,
    error,
    showAllSlots: shouldUseEnhanced,
    energyPreference: meetingLink?.energy_preference || 'focus',
    enhancedAvailableSlots
  }), [
    meetingLink,
    fontClass,
    themeCustomizations,
    step,
    selectedDate,
    selectedTime,
    selectedTimezone,
    handleBack,
    handleDateSelected,
    handleTimeSelected,
    setSelectedTimezone,
    handleFormSubmit,
    formData,
    customAnswers,
    guests,
    handleInputChange,
    handleCustomAnswerChange,
    setGuests,
    customQuestions,
    availableSlots,
    isDayAvailable,
    slotsLoading,
    shouldShowSkeleton,
    organizerTimezone,
    handlePrevDay,
    handleNextDay,
    debugSettings,
    troubleshootMode,
    loading,
    error,
    shouldUseEnhanced,
    enhancedAvailableSlots
  ]);

  // Show success screen if booking is submitted
  if (isSubmitted) {
    return (
      <BookingSuccessScreen
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        meetingLink={meetingLink}
        formData={formData}
        debugSettings={debugSettings}
        fontClass={fontClass}
        bookingId={bookingId}
      />
    );
  }

  // Render mobile or desktop layout
  if (forceMobileLayout || isMobile) {
    return <MobileBookingLayout {...layoutProps} />;
  }

  return <DesktopBookingLayout {...layoutProps} />;
});

ModernTemplateLogic.displayName = 'ModernTemplateLogic';
